import { useEffect, useState } from 'react';
import {
  AdminUser, PasswordReset, SentInvite, adminMe, createPlatformStaffApi, isInvitePending,
  listPlatformStaff, platformResendInviteApi, platformResetPasswordApi, setPlatformStaffActiveApi,
  setPlatformStaffRoleApi,
} from '../../api/admin';
import { PLATFORM_ROLE_LABEL, PlatformRole } from '../../shared/types';
import { Modal, RowCard, RowMenu, TableCard } from '../../components/fs';
import {
  ChangePasswordModal, InviteLinkModal, ResetPasswordModal, invitedMessage,
} from '../../components/PasswordDialogs';
import { usePlatform } from './PlatformLayout';
import { useToast } from '../../components/Toast';

/** ONE editor at a time (rule 6). */
type Editor =
  | { kind: 'add' }
  | { kind: 'mine' }
  | { kind: 'role' | 'active' | 'password'; userId: number }
  | null;

/**
 * What each rung of the ladder may do (D8) — the design's roles card,
 * rewritten for the three roles that exist. Every sentence is what
 * `server/src/lib/platformRoles.ts` enforces: Staff hold `accounts.read` and
 * `masquerade`; Admins add `accounts.manage`, `accounts.billing` (an
 * account's plan and price, its discounts, credits and free periods, and the
 * plan catalogue) and `staff.manage` (Staff-level colleagues only — see
 * `grantableRoles`); Super Admins hold everything, `staff.manageAdmins`,
 * `platform.storage` (every tab of Services), `site.content` and
 * `marketing.send` among it.
 */
export const ROLE_EXPLAINER: [PlatformRole, string][] = [
  ['staff',
    'Sees every account, the plans and the audit trail, and enters accounts in support mode. '
    + 'Changes no billing and hires nobody.'],
  ['admin',
    'Everything Staff can, plus creating, editing and deleting accounts and their logins; each '
    + 'account’s plan, price, discounts, credits and free periods; the plan catalogue; and hiring, '
    + 're-roling and deactivating Staff.'],
  ['superadmin',
    'Everything, including other Admins, the platform services (Stripe, email, storage, public URLs, '
    + 'captcha, custom domains, AI), the public site’s content and marketing email.'],
];

/**
 * The people who run the service (R12, screen 42).
 *
 * Its own page rather than a section under the customer table: who works here
 * is not a fact about a customer, and the roster is what someone opens when
 * they are doing personnel rather than support.
 *
 * Everyone on the ladder can read it — knowing who to escalate to is part of
 * support. `grantableRoles` comes from the server rather than being derived
 * here, so the options on offer are exactly the ones the API will accept.
 */
export default function PlatformStaff() {
  const { me, role } = usePlatform();
  const [staff, setStaff] = useState<AdminUser[] | null>(null);
  const [grantable, setGrantable] = useState<PlatformRole[]>([]);
  const [error, setError] = useState('');
  const [editor, setEditor] = useState<Editor>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const toast = useToast();
  /** false on an install with no SMTP — no screen may offer to email a link. */
  const [canEmail, setCanEmail] = useState(false);
  /** set only when an invitation could not be emailed — see InviteLinkModal */
  const [handOver, setHandOver] = useState<SentInvite | null>(null);

  const load = () => listPlatformStaff()
    .then((r) => { setStaff(r.staff); setGrantable(r.grantableRoles); })
    .catch((e) => setError(e.message));

  useEffect(() => {
    load();
    adminMe().then((r) => setCanEmail(!!r.mailConfigured)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function write(fn: () => Promise<unknown>, doneMsg?: string) {
    setError('');
    try {
      await fn();
      setEditor(null);
      if (doneMsg) toast(doneMsg);
      load();
    } catch (err: any) { setError(err.message); }
  }

  /**
   * A write that ends in an invitation — hiring, or sending it again.
   *
   * The one outcome `write` cannot express is a link that could not be
   * emailed: it has to be handed over on screen rather than announced.
   */
  async function writeInvite(fn: () => Promise<{ invite: SentInvite }>) {
    setError(''); setOpenMenuId(null);
    try {
      const r = await fn();
      setEditor(null);
      if (r.invite.emailed) toast(invitedMessage(r.invite));
      else setHandOver(r.invite);
      load();
    } catch (err: any) { setError(err.message); }
  }

  const mayEdit = (u: AdminUser) =>
    !!u.platform_role && grantable.includes(u.platform_role) && u.email !== me;

  const editing = editor && 'userId' in editor
    ? (staff || []).find((u) => u.id === editor.userId) || null
    : null;

  return (
    <div className="pl-page pl-staff">
      <div className="fs-page-head pl-head">
        <div className="text">
          <h1>Staff</h1>
          <div className="fs-page-sub">The people who run the platform</div>
        </div>
        {/* A direct child of the head, so the phone rule makes it full width. */}
        {grantable.length > 0 && (
          <button className="fs-btn primary" onClick={() => setEditor({ kind: 'add' })}>+ Add staff</button>
        )}
      </div>
      {error && <div className="ad-err">{error}</div>}

      <TableCard
        className="pl-staff-card"
        footer={(
          <span>
            Role changes and deactivation live in each row&apos;s ⋯ menu and confirm before saving.
            {role === 'admin' && ' As an Admin you can hire Staff; creating other Admins is a Super Admin action.'}
          </span>
        )}
      >
        {!staff ? <div className="st-card-body st-muted">Loading…</div> : (
          <table className="fs-table stack pl-staff-table">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Role</th><th>Sign-in</th><th>Last active</th>
                <th className="fs-row-actions pl-staff-menu-col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {staff.map((u) => (
                <tr key={u.id} className={u.active === false ? 'pl-staff-off' : undefined}>
                  <td>
                    <span className="pl-staff-name">{u.name || u.email}</span>
                    {u.email === me && <span className="pl-staff-you"> · you</span>}
                    {u.active === false && <span className="fs-pill neutral pl-staff-state">deactivated</span>}
                  </td>
                  <td data-label="Email" className="pl-staff-email">{u.email}</td>
                  <td data-label="Role" className="pl-staff-role">
                    {u.platform_role ? PLATFORM_ROLE_LABEL[u.platform_role] : '—'}
                  </td>
                  <td data-label="Sign-in">
                    {u.sso_sub
                      ? <span className="fs-pill identity">SSO</span>
                      : isInvitePending(u)
                        ? <span className="fs-pill caution" title="Invited — they have not set a password yet">invited</span>
                        : <span className="fs-pill neutral">password</span>}
                  </td>
                  <td data-label="Last active" className="pl-staff-last" title="Their last sign-in">
                    {u.last_signin_at ? new Date(u.last_signin_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="fs-row-actions">
                    {/* Your own row always has a menu: changing your own
                        password needs no authority over anybody. */}
                    {(mayEdit(u) || u.email === me) && (
                      <RowMenu
                        open={openMenuId === u.id} label={u.name || u.email}
                        onToggle={(open) => setOpenMenuId(open ? u.id : null)}
                      >
                        {u.email === me && (
                          <button onClick={() => setEditor({ kind: 'mine' })}>Change password…</button>
                        )}
                        {mayEdit(u) && (
                          <>
                            <button onClick={() => setEditor({ kind: 'role', userId: u.id })}>Change role…</button>
                            {/* Somebody who has never set a password is being
                                invited, not reset. */}
                            {isInvitePending(u)
                              ? <button onClick={() => writeInvite(() => platformResendInviteApi(u.id))}>Resend invitation</button>
                              : <button onClick={() => setEditor({ kind: 'password', userId: u.id })}>Reset password…</button>}
                            <div className="rule" />
                            {u.active === false ? (
                              <button onClick={() => setEditor({ kind: 'active', userId: u.id })}>Reactivate…</button>
                            ) : (
                              <button className="danger" onClick={() => setEditor({ kind: 'active', userId: u.id })}>Deactivate…</button>
                            )}
                          </>
                        )}
                      </RowMenu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableCard>

      <section className="fs-card pl-roles" aria-labelledby="pl-roles-title">
        <div className="st-card-head"><h2 id="pl-roles-title">What each role can do</h2></div>
        {ROLE_EXPLAINER.map(([r, text]) => (
          <RowCard key={r} label={PLATFORM_ROLE_LABEL[r]} hint={r === role ? 'Your role' : undefined}>
            {text}
          </RowCard>
        ))}
      </section>

      {editor?.kind === 'add' && grantable.length > 0 && (
        <AddStaffModal grantable={grantable} onClose={() => setEditor(null)}
          onAdd={(body) => writeInvite(() => createPlatformStaffApi(body))} />
      )}
      {editor?.kind === 'role' && editing && (
        <ChangeRoleModal user={editing} grantable={grantable} onClose={() => setEditor(null)}
          onSave={(r) => write(() => setPlatformStaffRoleApi(editing.id, r))} />
      )}
      {editor?.kind === 'active' && editing && (
        <ActiveModal user={editing} onClose={() => setEditor(null)}
          onConfirm={() => write(() => setPlatformStaffActiveApi(editing.id, editing.active === false))} />
      )}
      {editor?.kind === 'password' && editing && (
        <ResetPasswordModal user={editing} canEmail={canEmail} onClose={() => setEditor(null)}
          onReset={(body: PasswordReset) => write(
            () => platformResetPasswordApi(editing.id, body),
            'sendLink' in body
              ? `Sent ${editing.email} a link to set a new password.`
              : `Set a new password for ${editing.email} — they are signed out everywhere until they use it.`
          )} />
      )}
      {editor?.kind === 'mine' && me && (
        <ChangePasswordModal email={me} onClose={() => setEditor(null)}
          onDone={() => { setEditor(null); toast('Your password has been changed.'); }} />
      )}
      {handOver && <InviteLinkModal invite={handOver} onClose={() => setHandOver(null)} />}
    </div>
  );
}

/**
 * Hire somebody onto the ladder.
 *
 * No password field. A colleague's credential should never be typed by the
 * person adding them — they are emailed a link and choose their own, exactly
 * as a customer's user is.
 */
function AddStaffModal({ grantable, onClose, onAdd }: {
  grantable: PlatformRole[];
  onClose: () => void;
  onAdd: (body: { email: string; name?: string; platformRole: PlatformRole }) => void;
}) {
  const [form, setForm] = useState({ email: '', name: '', platformRole: grantable[0] || 'staff' as PlatformRole });
  const valid = !!form.email.trim();
  return (
    <Modal title="Add staff" narrow onClose={onClose}
      footer={(
        <>
          <button className="fs-btn primary" disabled={!valid} onClick={() => onAdd(form)}>Send invitation</button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      <div className="fs-row-2">
        <div className="fs-field">
          <label htmlFor="pl-staff-email">Email</label>
          <input id="pl-staff-email" type="email" autoFocus value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="fs-field">
          <label htmlFor="pl-staff-name">Name</label>
          <input id="pl-staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
      </div>
      <div className="fs-field pl-staff-rolefield">
        <label htmlFor="pl-staff-role">Platform role</label>
        <select id="pl-staff-role" value={form.platformRole}
          onChange={(e) => setForm({ ...form, platformRole: e.target.value as PlatformRole })}>
          {grantable.map((r) => <option key={r} value={r}>{PLATFORM_ROLE_LABEL[r]}</option>)}
        </select>
      </div>
      <div className="fs-consequence pl-staff-consequence">
        They get an email with a link to choose their own password — you never see it. Staff can
        read everything and masquerade for support; provisioning customers and changing their terms
        starts at Admin.
      </div>
    </Modal>
  );
}

function ChangeRoleModal({ user, grantable, onClose, onSave }: {
  user: AdminUser;
  grantable: PlatformRole[];
  onClose: () => void;
  onSave: (role: PlatformRole) => void;
}) {
  const [next, setNext] = useState<PlatformRole>(user.platform_role || 'staff');
  const changed = next !== user.platform_role;
  return (
    <Modal title={`Change role — ${user.name || user.email}`} narrow onClose={onClose}
      footer={(
        <>
          <button className="fs-btn primary" disabled={!changed} onClick={() => onSave(next)}>Save role</button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      <div className="fs-field pl-staff-rolefield first">
        <label htmlFor="pl-staff-newrole">Platform role</label>
        <select id="pl-staff-newrole" value={next} onChange={(e) => setNext(e.target.value as PlatformRole)}>
          {grantable.map((r) => <option key={r} value={r}>{PLATFORM_ROLE_LABEL[r]}</option>)}
        </select>
      </div>
      <div className="fs-consequence pl-staff-consequence">
        {changed
          ? <><b>{user.platform_role ? PLATFORM_ROLE_LABEL[user.platform_role] : '—'} → {PLATFORM_ROLE_LABEL[next]}.</b>{' '}
            The change takes effect on their next request and lands in the audit trail under your name.</>
          : 'Pick a different role to move this person.'}
      </div>
    </Modal>
  );
}

function ActiveModal({ user, onClose, onConfirm }: {
  user: AdminUser;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const reactivating = user.active === false;
  return (
    <Modal title={`${reactivating ? 'Reactivate' : 'Deactivate'} — ${user.name || user.email}`} narrow onClose={onClose}
      footer={(
        <>
          <button
            className={`fs-btn ${reactivating ? 'primary' : 'danger'}`}
            onClick={onConfirm}
          >
            {reactivating ? 'Reactivate' : 'Deactivate'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}>
      <div className="fs-consequence">
        {reactivating
          ? <>{user.email} can sign in again with their existing credentials. The change lands in the audit trail under your name.</>
          : <>{user.email} is signed out and cannot sign in until reactivated. Nothing is deleted. The change lands in the audit trail under your name.</>}
      </div>
    </Modal>
  );
}
